import { useCallback, type ComponentType, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import { useContactTracking } from '@/hooks/useContactTracking';
import {
  formatHoursDisplayLabel,
  type StoreOpenStatus,
  type StoreWeekdayHours,
} from '@/lib/boutique/hours';
import type { Weekday } from '@/lib/const';
import { modalActions } from '@/states/modalState';
import clsx from 'clsx';

import styles from './BoutiqueInfo.module.scss';

interface ChevronLinkIconProps {
  chevronIcon: ReactNode;
}

export interface BoutiqueInfoAddressColumnProps {
  addressLines: string[];
  countryLabel: string;
  mapsUrl: string;
  directionsLabel: string;
  addressTitle: string;
  testId: (suffix: string) => string;
}

export function BoutiqueInfoAddressColumn({
  addressLines,
  countryLabel,
  mapsUrl,
  directionsLabel,
  addressTitle,
  testId,
  chevronIcon,
}: Readonly<BoutiqueInfoAddressColumnProps & ChevronLinkIconProps>) {
  return (
    <div className={styles.column}>
      <h3 className={styles.columnTitle}>{addressTitle}</h3>
      <address className={styles.address}>
        {addressLines.map((line) => (
          <span key={line} className={styles.addressLine}>
            {line}
          </span>
        ))}
        <span className={styles.addressLine}>{countryLabel}</span>
      </address>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.textLink}
        data-testid={testId('directions')}
      >
        {directionsLabel}
        {chevronIcon}
      </a>
    </div>
  );
}

export interface BoutiqueInfoContactColumnProps {
  phone?: string;
  email?: string;
  phoneLabel: string;
  emailFieldLabel: string;
  emailLabel: string;
  contactTitle: string;
  testId: (suffix: string) => string;
}

export function BoutiqueInfoContactColumn({
  phone,
  email,
  phoneLabel,
  emailFieldLabel,
  emailLabel,
  contactTitle,
  testId,
}: Readonly<BoutiqueInfoContactColumnProps>) {
  const { trackPhoneContact, trackEmailContact } = useContactTracking();

  return (
    <div className={styles.column}>
      <h3 className={styles.columnTitle}>{contactTitle}</h3>
      <div className={styles.contact}>
        {phone ? (
          <p className={styles.contactLine}>
            <span className={styles.contactLabel}>{phoneLabel}</span>
            <a
              href={`tel:${phone}`}
              className={styles.contactLink}
              data-testid={testId('phone')}
              onClick={trackPhoneContact}
            >
              {phone}
            </a>
          </p>
        ) : null}
        {email ? (
          <p className={styles.contactLine}>
            <span className={styles.contactLabel}>{emailFieldLabel}</span>
            <a
              href={`mailto:${email}`}
              className={styles.contactLink}
              data-testid={testId('email')}
              onClick={trackEmailContact}
            >
              {emailLabel}
            </a>
          </p>
        ) : null}
      </div>
    </div>
  );
}

export interface BoutiqueInfoHoursColumnProps {
  weekSchedule: StoreWeekdayHours[];
  openStatus: StoreOpenStatus;
  weekdayLabel: (weekday: Weekday) => string;
  openingHoursTitle: string;
  temporaryClosedLabel: string;
  openNowLabel: string;
  closedLabel: string;
}

export function BoutiqueInfoHoursColumn({
  weekSchedule,
  openStatus,
  weekdayLabel,
  openingHoursTitle,
  temporaryClosedLabel,
  openNowLabel,
  closedLabel,
}: Readonly<BoutiqueInfoHoursColumnProps>) {
  const openDays = weekSchedule.filter((entry) => entry.hoursLabel);

  return (
    <div className={styles.column}>
      <h3 className={styles.columnTitle}>{openingHoursTitle}</h3>
      {openDays.length > 0 ? (
        <ul className={styles.hoursList}>
          {openDays.map((entry) => (
            <li key={entry.weekday} className={styles.hoursRow}>
              <span className={styles.weekday}>{weekdayLabel(entry.weekday)}</span>
              <span className={styles.hoursValue}>{formatHoursDisplayLabel(entry.hoursLabel)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.hoursFallback}>{temporaryClosedLabel}</p>
      )}

      {openStatus.status === 'temporary' ? (
        <p className={styles.hoursFallback}>{temporaryClosedLabel}</p>
      ) : (
        <div className={styles.statusRow}>
          <span
            className={clsx(styles.statusDot, styles[`statusDot_${openStatus.status}`])}
            aria-hidden="true"
          />
          <span
            className={clsx(
              styles.statusText,
              openStatus.status === 'open' && styles.statusText_open
            )}
          >
            {openStatus.status === 'open' ? openNowLabel : closedLabel}
          </span>
        </div>
      )}
    </div>
  );
}

export interface BoutiqueServiceItem {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

export interface BoutiqueInfoAppointmentCtaProps {
  storeId: string;
  label: string;
  testId: (suffix: string) => string;
  chevronIcon: ReactNode;
}

export function BoutiqueInfoAppointmentCta({
  storeId,
  label,
  testId,
  chevronIcon,
}: Readonly<BoutiqueInfoAppointmentCtaProps>) {
  const { sendIntentionEvent } = useGoogleAnalytics();

  const handleBookAppointment = useCallback(() => {
    sendIntentionEvent(EVENT_NAME.INTENTION_BOOK_APPT);
    modalActions.open('book-appointment', { storeId });
  }, [sendIntentionEvent, storeId]);

  return (
    <div className={styles.ctaWrap}>
      <Button
        type="button"
        variant="secondary"
        size="md"
        className={styles.ctaButton}
        iconAfter={chevronIcon}
        onClick={handleBookAppointment}
        data-testid={testId('book-appointment')}
      >
        {label}
      </Button>
    </div>
  );
}

export interface BoutiqueInfoServicesSectionProps {
  services: BoutiqueServiceItem[];
  servicesHeading: string;
}

export function BoutiqueInfoServicesSection({
  services,
  servicesHeading,
}: Readonly<BoutiqueInfoServicesSectionProps>) {
  return (
    <section className={styles.servicesSection} aria-label={servicesHeading}>
      <ul className={styles.servicesGrid}>
        {services.map((service) => {
          const ServiceIcon = service.icon;
          return (
            <li key={service.key} className={styles.serviceItem}>
              <ServiceIcon className={styles.serviceIcon} />
              <span className={styles.serviceLabel}>{service.label}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
